import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ID_PERFIL_VENDEDOR } from '../../constants/perfiles.js'
import { puedeAgregarDomicilio } from '../../helpers/domicilioPermisos'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth'
import { resetModal, setModal } from '../../slices/modalSlice.js'
import { usePaginacion } from '../hooks/usePaginacion'
import { GenericModal } from '../reutilizables/modal/GenericModal.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion'
import * as domiciliosService from '../servicios/domiciliosService'
import Input from '../ui/Input'
import stylesDomicilio from './AdministrarDomicilios.module.css'
import Domicilio from './Domicilio.jsx'
import ModalDomicilio from './ModalDomicilio.jsx'
import styles from './Perfil.module.css'

const ITEMS_POR_PAGINA = 30
const ITEMS_PERFIL = 7

const coincideBusqueda = (domicilio, busqueda) => {
  const texto = busqueda.trim().toLowerCase()
  if (!texto) return true

  return [
    domicilio.idUsuarioDomicilio,
    domicilio.tipoDomicilio,
    domicilio.calle,
    domicilio.calleAltura,
    domicilio.provincia,
    domicilio.ciudad
  ]
    .filter(Boolean)
    .some((campo) => String(campo).toLowerCase().includes(texto))
}

const Domicilios = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [busqueda, setBusqueda] = useState('')
  const [seleccionados, setSeleccionados] = useState([])
  const [mostrarModal, setMostrarModal] = useState(false)
  const [domicilioSeleccionado, setDomicilioSeleccionado] = useState(null)
  const [domicilioAEliminar, setDomicilioAEliminar] = useState(null)
  const [domicilios, setDomicilios] = useState([])

  const { loginDataContext: { idUsuario, tipoUsuario, idPerfil } = {}, cargandoUsuario } = useAuth()
  const puedeSeleccionarPreferidos = idPerfil !== ID_PERFIL_VENDEDOR
  const esAdministrarDomicilio = location.pathname === '/AdministrarDomicilios'

  const domiciliosFiltrados = useMemo(
    () => domicilios.filter((d) => coincideBusqueda(d, busqueda)),
    [domicilios, busqueda]
  )

  const hayCambiosPendientes = useMemo(
    () =>
      domicilios.some(
        (d) => Boolean(d.esPreferido) !== seleccionados.includes(d.idUsuarioDomicilio)
      ),
    [domicilios, seleccionados]
  )

  const {
    itemsActual: domiciliosPaginados,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(domiciliosFiltrados, ITEMS_POR_PAGINA)

  const domiciliosVisibles = esAdministrarDomicilio
    ? domiciliosPaginados
    : domiciliosFiltrados.slice(0, ITEMS_PERFIL)

  const cargarDomicilios = useCallback(async () => {
    const { data } = await domiciliosService.buscarTodas(idUsuario, tipoUsuario)
    const domicilios = data.length > 0 ? data : []
    setDomicilios(domicilios)
    setSeleccionados(domicilios?.filter((d) => d.esPreferido).map((d) => d.idUsuarioDomicilio))
  }, [idUsuario, tipoUsuario])

  useEffect(() => {
    if (!idUsuario || !tipoUsuario || cargandoUsuario) return
    cargarDomicilios()
  }, [cargarDomicilios])

  const handleDomicilioActualizado = (domicilioActualizado) => {
    setDomicilios((prev) =>
      prev.map((d) =>
        d.idUsuarioDomicilio === domicilioActualizado.idUsuarioDomicilio
          ? { ...d, ...domicilioActualizado }
          : d
      )
    )
  }

  const handleDomicilioAgregado = (domicilioInsertado) => {
    setDomicilios((prev) => [...prev, domicilioInsertado])
  }

  const toggleSeleccionado = (idUsuarioDomicilio) => {
    setSeleccionados((prev) =>
      prev.includes(idUsuarioDomicilio)
        ? prev.filter((id) => id !== idUsuarioDomicilio)
        : [...prev, idUsuarioDomicilio]
    )
  }

  const guardarPreferidos = async () => {
    const idUsuarioDomicilios = seleccionados.map((id) => String(id))
    await domiciliosService.guardarPreferidos(idUsuario, idUsuarioDomicilios)
    toast.success('Preferidos guardados correctamente.')
    setDomicilios((prev) =>
      prev.map((domicilio) => ({
        ...domicilio,
        esPreferido: seleccionados.includes(domicilio.idUsuarioDomicilio)
      }))
    )
  }

  const abrirModalEliminacion = (tipoDomicilio, idUsuarioDomicilio) => {
    setDomicilioAEliminar(idUsuarioDomicilio)
    dispatch(
      setModal({
        size: 'sm',
        title: 'Eliminar domicilio',
        type: 'botones',
        modalId: 'eliminar-domicilio',
        body: `¿Estás seguro que deseas eliminar el domicilio "${tipoDomicilio}"?`,
        show: true
      })
    )
  }

  const eliminarDomicilio = async () => {
    try {
      await domiciliosService.eliminarDomicilio(domicilioAEliminar)
      setDomicilios((prev) =>
        prev.filter((domicilio) => domicilio.idUsuarioDomicilio !== domicilioAEliminar)
      )
      setSeleccionados((prev) => prev.filter((id) => id !== domicilioAEliminar))
      setDomicilioAEliminar(null)
      dispatch(resetModal())
    } catch (error) {
      const mensaje = 'Ocurrió un error al eliminar el domicilio. Por favor, inténtalo nuevamente.'
      toastErrorLog(error, mensaje, 'domicilio')
    }
  }

  const abrirModal = (domicilio) => {
    setDomicilioSeleccionado(domicilio)
    setMostrarModal(true)
  }

  const abrirModalAlta = () => {
    setDomicilioSeleccionado(null)
    setMostrarModal(true)
  }

  const cerrarModal = () => {
    dispatch(resetModal())
    setMostrarModal(false)
    setDomicilioSeleccionado(null)
  }

  const listaDomicilios =
    domiciliosVisibles.length === 0 ? (
      <p className={stylesDomicilio.emptyState}>No se encontraron domicilios.</p>
    ) : (
      domiciliosVisibles.map((domicilio) => (
        <Domicilio
          key={domicilio.idUsuarioDomicilio}
          domicilio={domicilio}
          abrirModal={abrirModal}
          esAdministrarDomicilio={esAdministrarDomicilio}
          seleccionados={seleccionados}
          toggleSeleccionado={toggleSeleccionado}
          eliminarDomicilio={abrirModalEliminacion}
          puedeSeleccionarPreferidos={puedeSeleccionarPreferidos}
        />
      ))
    )

  return (
    <>
      {esAdministrarDomicilio ? (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Domicilios</h3>
          <div className={stylesDomicilio.buscadorRow}>
            <Input
              type="text"
              name="busquedaDomicilio"
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value)
                setPaginaActual(1)
              }}
              className={`input-text-bordered ${stylesDomicilio.buscadorDomicilios}`}
              placeholder="Buscar por calle, provincia, localidad..."
            />
            {puedeSeleccionarPreferidos && (
              <button
                type="button"
                className={stylesDomicilio.botonPreferido}
                disabled={!hayCambiosPendientes}
                onClick={guardarPreferidos}
              >
                Actualizar preferidos {`(${seleccionados.length})`}
              </button>
            )}
            {puedeAgregarDomicilio(tipoUsuario) && (
              <button
                type="button"
                className={stylesDomicilio.botonPreferido}
                onClick={abrirModalAlta}
              >
                Agregar domicilio
              </button>
            )}
          </div>
          {puedeSeleccionarPreferidos && (
            <p className={stylesDomicilio.avisoPreferidos}>
              Los domicilios seleccionados como preferidos se mostrarán en el listado de domicilios
              al momento de realizar un pedido.
            </p>
          )}
          <div
            className={`${stylesDomicilio.domiciliosListCompleta} ${stylesDomicilio.domiciliosGrid}`}
          >
            {listaDomicilios}
          </div>
          {domiciliosFiltrados.length > 0 && (
            <Paginacion
              totalPaginas={totalPaginas}
              paginaActual={paginaActual}
              onCambioPagina={setPaginaActual}
              itemsPorPagina={itemsPorPagina}
              onCambioItemsPorPagina={(num) => {
                setItemsPorPagina(num)
                setPaginaActual(1)
              }}
            />
          )}
        </div>
      ) : (
        <div className={styles.container}>
          <h3 className={styles.cardTitle}>Domicilios</h3>
          <div className={stylesDomicilio.domiciliosScroll}>{listaDomicilios}</div>
          <div className={styles.btnContainer}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutlinePrimary}`}
              onClick={() => navigate('/AdministrarDomicilios')}
            >
              Administrar Domicilios
            </button>
          </div>
        </div>
      )}
      <ModalDomicilio
        show={mostrarModal}
        domicilio={domicilioSeleccionado}
        domicilios={domicilios}
        onClose={cerrarModal}
        onDomicilioAgregado={handleDomicilioAgregado}
        onDomicilioActualizado={handleDomicilioActualizado}
      />
      <GenericModal
        modalId={'eliminar-domicilio'}
        onConfirm={eliminarDomicilio}
        onClose={cerrarModal}
      />
    </>
  )
}

export default Domicilios
